import { useEffect, useRef, useState } from 'react';
import { Paperclip, Loader2, Lock } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const isImageFile = (s) => /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(s || '');

/**
 * A homework attachment, fetched with the viewer's own sign-in rather than by open URL.
 *
 * Homework used to hang off a public address, so a member's written work could be opened by
 * anyone the link reached, member or not. The file is now requested with the sign-in token, the
 * server decides whether this person may have it, and the bytes are held in memory for the life
 * of the page — there is no address to copy, forward or leave behind in a history.
 */
export const HomeworkAttachment = ({ url, name, testIdSuffix = '' }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [state, setState] = useState('loading');
  const created = useRef(null);

  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    // A replaced file must not keep showing the previous one.
    if (created.current) {
      URL.revokeObjectURL(created.current);
      created.current = null;
    }
    setBlobUrl(null);
    setState('loading');
    (async () => {
      try {
        const res = await fetch(`${BACKEND}${url}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        created.current = objectUrl;
        setBlobUrl(objectUrl);
        setState('ready');
      } catch {
        if (!cancelled) setState('denied');
      }
    })();

    return () => {
      cancelled = true;
      if (created.current) URL.revokeObjectURL(created.current);
    };
  }, [url]);

  if (!url) return null;

  if (state === 'loading') {
    return (
      <p className="mt-2 inline-flex items-center gap-1 text-xs text-gray-400"
         data-testid={`hw-attachment-loading${testIdSuffix}`}>
        <Loader2 className="w-3 h-3 animate-spin" /> Opening attachment…
      </p>
    );
  }

  if (state === 'denied') {
    return (
      <p className="mt-2 inline-flex items-center gap-1 text-xs text-gray-500"
         data-testid={`hw-attachment-denied${testIdSuffix}`}>
        <Lock className="w-3 h-3" /> This attachment isn&apos;t available to you
      </p>
    );
  }

  if (isImageFile(name || url)) {
    return (
      <a href={blobUrl} target="_blank" rel="noreferrer"
         data-testid={`hw-attachment-image${testIdSuffix}`}>
        <img src={blobUrl} alt={name || 'attachment'}
             className="mt-2 rounded-lg max-h-52 border object-contain" />
      </a>
    );
  }

  return (
    <a href={blobUrl} target="_blank" rel="noreferrer" download={name || 'attachment'}
       className="mt-2 inline-flex items-center gap-1 text-xs text-cyan-700 underline"
       data-testid={`hw-attachment-file${testIdSuffix}`}>
      <Paperclip className="w-3 h-3" /> {name || 'Attachment'}
    </a>
  );
};

export default HomeworkAttachment;
